import { RequirementDesignElement } from "@design-controls/types";
import React, { useMemo } from "react";
import {
  DataProvider,
  QDataTable,
  QIcon,
  QStack,
  QText,
} from "@qualio/ui-components";
import { sanitize } from "dompurify";
import diff from "../htmldiff/htmldiff";
import { useTranslation } from "react-i18next";

type Props = {
  base: RequirementDesignElement;
  compare: RequirementDesignElement;
};

type Column = {
  label: string;
  base: any;
  compare: any;
};

export const Requirement: React.FC<Props> = ({ base, compare }) => {
  const { t } = useTranslation();
  const comparison = diff(base?.description ?? "", compare?.description ?? "");

  const rows = useMemo(() => {
    const rows: Column[] = [];

    if (base.title !== compare.title) {
      rows.push({
        label: t("diffView.fields.title"),
        base: base.title,
        compare: compare.title,
      });
    }

    if (base.component?.qri !== base.component?.qri) {
      rows.push({
        label: t("diffView.fields.component"),
        base: base.component?.label,
        compare: compare.component?.label,
      });
    }

    if (base.category?.qri !== base.category?.qri) {
      rows.push({
        label: t("diffView.fields.category"),
        base: base.category?.label,
        compare: compare.category?.label,
      });
    }

    return rows;
  }, [base, compare, t]);

  const columns = useMemo(() => {
    return [
      {
        accessorKey: "label",
        header: t("diffView.table.header.label"),
        meta: {
          width: "200px",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) => (
          <QText fontWeight={600}>{row.label}</QText>
        ),
      },
      {
        accessorKey: "base",
        header: t("diffView.table.header.base"),
        meta: {
          minWidth: "200px",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) =>
          row.base ?? (
            <QText color="gray.500">{t("diffView.table.empty")}</QText>
          ),
      },
      {
        accessorKey: "id",
        header: t("diffView.table.header.icon"),
        meta: {
          width: "60px",
        },
        cell: () => <QIcon iconName="ArrowRight" />,
      },
      {
        accessorKey: "compare",
        header: t("diffView.table.header.compare"),
        meta: {
          minWidth: "200px",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) =>
          row.compare ?? (
            <QText color="gray.500">{t("diffView.table.empty")}</QText>
          ),
      },
    ];
  }, [t]);

  return (
    <QStack gap="16px">
      {rows.length && (
        <DataProvider.Fixed data={rows}>
          <QDataTable columns={columns} hideItemCount withHeaders={false} />
        </DataProvider.Fixed>
      )}
      {base.description !== compare.description && (
        <QStack>
          <QText fontSize="sm" fontWeight={600} color="gray.900">
            {t("diffView.fields.description")}
          </QText>

          <QText
            fontSize={14}
            style={{ wordBreak: "break-word" }}
            dangerouslySetInnerHTML={{
              __html: sanitize(comparison),
            }}
          />
        </QStack>
      )}
    </QStack>
  );
};
